import { Component, OnInit, OnDestroy, AfterViewInit, Input, Output, EventEmitter, ChangeDetectorRef, ViewChild, ElementRef, TemplateRef, EmbeddedViewRef } from '@angular/core';
import { FormControl, Validators, ValidatorFn, ValidationErrors } from '@angular/forms';
import { Overlay, OverlayRef, OverlayConfig } from '@angular/cdk/overlay';
import { TemplatePortalDirective } from '@angular/cdk/portal';

import { FieldComponent, UndoAction } from './field.component';
import { FormField, Link, Disclaimer } from './form-field.model';
import { DynamicOverlaysDirective } from './dynamic-overlays.directive';

export class TokenItem {
  icon?: string;
  loading: boolean = false;
  locked: boolean = false;
  toolTip?: string;
  value?: any;
  valid: boolean = false;
}

@Component({
  selector: 'app-tokens-field',
  templateUrl: './tokens-field.component.html',
  styleUrls: ['./tokens-field.component.scss']
})
export class TokensFieldComponent extends FieldComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() draftData: any;
  @Input() dynamicOverlays: DynamicOverlaysDirective;
  @Input() field: FormField;
  @Input() locked: boolean = false;
  @Input() lockValues: boolean = false;
  @Input() templateData: any;
  @Input() workflowId: number;
  @Output() focusChanged = new EventEmitter<boolean>();
  @Output() highlightChanged = new EventEmitter<{highlighted: boolean, color?: string}>();
  @Output() resized = new EventEmitter<FieldComponent>();
  @Output() returnKey = new EventEmitter<FieldComponent>();
  @Output() undoableAction = new EventEmitter<UndoAction>();
  @Output() updateLinked = new EventEmitter<{link: Link, value?: any, categories?: any[], enabled?: boolean}>();
  @Output() viewLoaded = new EventEmitter<FieldComponent>();
  @ViewChild("versionTemplate") versionTemplate: TemplateRef<null>;
  @ViewChild("versionElement") versionElement: ElementRef;

  _errors: ValidationErrors;
  debounce: number = 0;
  defaultValue: any[];
  disclaimer: Disclaimer;
  focused: boolean = false;
  hasInputDialog: boolean = false;
  highlightColor: string;
  highlighted: boolean = false;
  id: string;
  items: TokenItem[] = [];
  label: string;
  loaded: boolean = false;
  max: number;
  min: number;
  placeholder: string;
  pattern: string;
  patternRegex: RegExp;
  previousItems: TokenItem[];
  required: boolean = false;
  showingPreviousValue: boolean = false;
  touched: boolean = false;
  valid: boolean = false;
  versionChanged: boolean = false;
  versionView: EmbeddedViewRef<null>;

  get blank() { return this.items.length == 0 }
  get errors(): ValidationErrors { return this._errors }
  get placeholderText(): string {return this.items.length > 0 ? '' : this.placeholder};
  get value() { return this.items.map((i) => i.value.stored) }

  constructor(
    cd: ChangeDetectorRef,
    public overlay: Overlay) { super(cd) }

  ngOnInit() {
    if (this.id == null) {this.id = this.field.id}
    if (this.pattern == null) {this.pattern = this.field.pattern}
    if (this.placeholder == null) {this.placeholder = this.field.placeholder}
    this.label = this.field.label;
    this.required = this.field.required;
    if (this.required) {this.min = this.field.min}
    this.max = this.field.max;
    if (this.field.locked) {this.locked = true};

    if (this.pattern != null) {
      this.patternRegex = new RegExp(this.pattern, 'i');
    }

    this.loadDefaultValue();
    this.loadInitialState(this.draftData);
  }

  ngOnDestroy() {
    this.hidePreviousValue();
  }

  ngAfterViewInit() {
    this.loaded = true;
    this.viewLoaded.emit(this);
  }

  loadDefaultValue() {
    let value = [];
    if (this.templateData != null && this.templateData.length > 0) {
      value = this.templateData
    } else if (this.field.defaultValue != null) {
      value = [this.field.defaultValue];
    }
    this.defaultValue = value.map(
      (v) => v.hasOwnProperty("stored") ? v : {stored: v, displayed: v}
    )
  }

  loadInitialState(draftValue?: any[]) {
    this._dirty = false;
    this.touched = false;
    
    let value: any[];
    if (draftValue != null) {
      value = draftValue.map(
        (v) => v.hasOwnProperty("stored") ? v : {stored: v, displayed: v}
      )
    } else {
      value = this.defaultValue;
    }
    this.items = this.loadItems(value);
    if (this.lockValues) {
      for (let item of this.items) { item.locked = true }
    }

    this.control = new FormControl({value: null, disabled: this.locked}, []);
    this.control.valueChanges.debounceTime(this.debounce).subscribe(
      (value) => this.onValueChange(value)
    )

    this.validate();
    this.emitValue();
  }

  loadItems(value?: any[]): TokenItem[] {
    let items = [];
    if (value != null) {
      for (let v of value) {
        let item = new TokenItem();
        item.value = v;
        item.valid = true;
        items.push(item);
      }
    }
    return items;
  }

  validate() {
    if (this.locked == true) {
      this._errors = null;
      this.valid = true;
      return;
    }

    let valid = true;
    let errors = {};
    let validators: ValidatorFn[] = [
      this.validateRequired,
      this.validateMin,
      this.validateMax,
      this.validateItems
    ]
    for (let validator of validators) {
      let error = validator.call(this, this.control);
      if (error != null) {
        valid = false;
        for (let key in error) {
          errors[key] = error[key];
        }
      }
    }

    this.valid = valid;
    this._errors = !valid ? errors : null;
  }

  validateRequired(control): {[key: string]: any} | null {
    if (this.required && this.items.length < 1) {
      return {'required': true};
    }
  }

  validateMin(control): {[key: string]: any} | null {
    if (this.min != null && this.items.length < this.min) {
      return {'min': {min: this.min, current: this.items.length}};
    }
  }

  validateMax(control): {[key: string]: any} | null {
    if (this.max != null && this.items.length > this.max) {
      return {'max': {max: this.max, current: this.items.length}};
    }
  }

  validateItems(control): {[key: string]: any} | null {
    let invalidItems = this.items.filter((i) => !i.valid)
    if (invalidItems.length > 0) {
      return {'items': {items: invalidItems}};
    }
  }

  validateItem(item) {
    item.valid = (this.patternRegex == null || this.patternRegex.test(item.value.stored))
  }

  descriptionForError(key, value): string {
    switch (key) {
    case 'items':
      return 'has ' + value.items.length + ' invalid item' + (value.items.length != 1 ? 's' : '');
    case 'max':
      return 'must have fewer than ' + value.max + ' item' + (value.max != 1 ? 's' : '');
    case 'min':
      return 'must have at least ' + value.min + ' item' + (value.min != 1 ? 's' : '');
    default:
      return super.descriptionForError(key, value);
    }
  }

  valuesEqual(a,b): boolean {
    if (a == null || b == null) {
      return a == b;
    } else if (a.length != b.length) {
      return false;
    } else {
      for (let i = 0 ; i < a.length ; i++) {
        if (!this.itemValuesEqual(a[i], b[i])) {
          return false;
        }
      }
      return true;
    }
  }

  itemValuesEqual(a,b): boolean {
    a = a != null ? a.stored : null;
    b = b != null ? b.stored : null;
    if (a == null || b == null) {
      return a == b;
    } else {
      return a.caseInsensitiveCompare(b);
    }
  }

  itemExists(value): boolean {
    return (this.items.find((i) => this.itemValuesEqual(i.value, value)) != null)
  }

  commitValue() {
    let value = this.control.value;
    if (value != null && value.trim() !== '') {
      let item = this.itemFromControlValue(value);
      this.validateItem(item);
      this.addItem(item);
      this.control.setValue('');
      setTimeout(() => this.resized.emit(this), 100);
    }
  }

  itemFromControlValue(value: string): TokenItem {
    let item = new TokenItem();
    item.value = {
      stored: value,
      displayed: value
    };
    return item;
  }

  addItem(item) {
    if (!this.itemExists(item.value)) {
      this.items.push(item);
      this._dirty = true;
      this.validate();
      this.emitValue();
      this.cd.markForCheck();
    }
  }

  onRemoveItem(item) {
    if (item.locked) { return }
    this.removeItem(item);
    setTimeout(() => this.resized.emit(this), 100);
  }

  removeItem(item) {
    let index = this.items.indexOf(item);
    if (index !== -1) {
      this.items.splice(index,1);
      this._dirty = true;
      this.validate();
      this.emitValue();
    }
  }

  emitValue() {
    this.valueChanged.emit({field: this.id, value: this.value})
  }

  setValue(value: any, emit: boolean = true) {
    this.items = this.loadItems(value);
    this.validate();
    if (emit) { this.emitValue(); }
  }

  setPreviousValue(value) {
    this.previousItems = this.loadItems(value);
    if (this.previousItems.length != this.items.length) {
      this.versionChanged = true;
    } else {
      let changed = false;
      let i = 0;
      for (let item of this.items) {
        if (!this.itemValuesEqual(this.previousItems[i], this.items[i])) {
          changed = true;
          break;
        }
        i += 1;
      }
      this.versionChanged = changed;
    }
    if (this.versionChanged) {
      this.showPreviousValue();
    }
    this.cd.markForCheck();
  }

  resetPreviousValue() {
    this.previousItems = null;
    this.versionChanged = false;
    this.hidePreviousValue();
    this.cd.markForCheck();
  }

  onValueChange(value) {
  }

  onFocus(event) {
    this.focused = true;
    this.focusChanged.emit(true);
    return true;
  }

  onBlur(event) {
    this.commitValue();
    this.focused = false;
    this.touched = true;
    this.focusChanged.emit(false);
    return true;
  }

  onKey(event) {
    switch (event.keyCode) {
    case 8:
      return this.onBackspaceKey();
    case 9:
      return this.onTabKey();
    case 13:
      return this.onReturnKey();
    case 32:
      return this.onSpaceKey();
    case 38:
      return this.onUpArrowKey();
    case 40:
      return this.onDownArrowKey();
    default: 
      return true;
    }
  }

  onBackspaceKey() {
    if (this.control.value == null || this.control.value.length === 0) {
      if (this.items.length > 0) {
        this.onRemoveItem(this.items[this.items.length-1]);
      }
      return false;
    } else {
      return true;
    }
  }

  onTabKey() {
    let value = this.control.value;
    if (value != null && value.trim() !== '') {
      this.commitValue();
      return false;
    } else {
      return true;
    }
  }

  onReturnKey() {
    let value = this.control.value;
    if (value != null && value.trim() !== '') {
      this.commitValue();
    }
    return false;
  }

  onSpaceKey() {
    return true;
  }

  onUpArrowKey() {
    return true;
  }

  onDownArrowKey() {
    return true;
  }

  showInputDialog() { }

  reset() {
    this.loadInitialState();
    this.cd.markForCheck();
  }

  compare(other): number {
    let a = this.items[0] != null && this.items[0].value != null && this.items[0].value.displayed != null ? this.items[0].value.displayed : '';
    let b = other.items[0] != null && other.items[0].value != null && other.items[0].value.displayed != null ? other.items[0].value.displayed : '';
    if (a === '') {
      return 1;
    } else if (b === '') {
      return -1;
    } else {
      return a.localeCompare(b, 'us', {sensitivity: "base", ignorePunctuation: true});
    }
  }

  updateLinkedFields() { }
  
  setCategories(category) { }

  showPreviousValue() {
    this.showingPreviousValue = true;
    let container = this.dynamicOverlays.viewContainerRef;
    this.versionView = container.createEmbeddedView(this.versionTemplate);
    this.positionOverlays();
  }

  hidePreviousValue() {
    if (this.versionView != null) {
      this.versionView.destroy();
    }
    this.showingPreviousValue = false;
  }

  togglePreviousValue() {
    if (this.showingPreviousValue) {
      this.hidePreviousValue();
    } else {
      this.showPreviousValue();
    }
  }

  positionOverlays() {
    if (this.versionView != null) {
      let rect = this.versionElement.nativeElement.getBoundingClientRect();
      let rootNode = this.versionView.rootNodes.find((n) => n.tagName == 'DIV');
      rootNode.style.left = '' + (rect.left + window.pageXOffset) + 'px';
      rootNode.style.top = '' + (rect.top + window.pageYOffset) + 'px';
      rootNode.style.maxWidth = '' + rect.width + 'px';
    }
  }

  setPatternValue(field: string, value: any) { }

  undo(action: UndoAction) { }

  setLockValues(state) {
    this.lockValues = state;
    for (let item of this.items) {
      item.locked = this.lockValues;
    }
    this.cd.markForCheck();
  }

  valueFromTemplate(templateValue) {
    return templateValue != null ? this.loadItems(templateValue) : null;
  }
}


// WEBPACK FOOTER //
// ./src/app/forms/tokens-field.component.ts